'use client'

import { useIntl } from 'react-intl'
import { useEffect, MouseEvent } from 'react'
import { Dialog, Cell, Text, Button, Divider, Navigation } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import {
  CurrencyConversionWithMoneyObjectModel,
  formatCurrency,
  formatCurrencyAmount,
} from '@marketplace-web/currency/currency-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { clickEvent } from '@marketplace-web/bundles/items-bundle-data'

type Props = {
  shippingPrice?: string | null
  show: boolean
  price: string
  buyerProtectionFee: string
  bundleTrackingId: string | null
  isIntegratedShipping: boolean
  conversion: CurrencyConversionWithMoneyObjectModel
  onClose: () => void
}

const CurrencyConversionModal = ({
  show,
  price,
  onClose,
  conversion,
  shippingPrice,
  bundleTrackingId,
  buyerProtectionFee,
  isIntegratedShipping,
}: Props) => {
  const { formatNumber, locale } = useIntl()
  const translate = useTranslate('currency_conversion')
  const { track } = useTracking()

  const exchangeRateCopyCheckoutABTest = useAbTest('exchange_fee_copy_bundles')
  const trackAbTest = useTrackAbTestCallback()

  useEffect(() => {
    if (show) {
      trackAbTest(exchangeRateCopyCheckoutABTest)
    }
  }, [exchangeRateCopyCheckoutABTest, show, trackAbTest])

  const rootTestId = 'currency-conversion-modal'

  function renderNavigation() {
    return (
      <Navigation
        testId={`${rootTestId}--navigation`}
        body={translate('title')}
        right={
          <Button
            inline
            iconName={X24}
            styling="flat"
            onClick={onClose}
            testId={`${rootTestId}--close-x-icon`}
          />
        }
      />
    )
  }

  function renderItemPriceConversionInfo() {
    const { sellerPrice, buyerCurrency } = conversion

    const priceBeforeConversion = formatCurrencyAmount(sellerPrice, locale)

    const priceAfterConversion = formatCurrency(price, {
      currency: buyerCurrency,
      locale,
    })

    return (
      <Cell
        title={translate('price')}
        body={
          <Text
            as="span"
            html
            text={translate('price_description', { cost: priceBeforeConversion })}
          />
        }
        suffix={priceAfterConversion}
        testId={`${rootTestId}--item-price-conversion-info`}
      />
    )
  }

  function renderShippingPriceConversionInfo() {
    const { sellerShippingPrice, buyerCurrency } = conversion

    if (sellerShippingPrice == null || shippingPrice == null) return null

    const priceBeforeConversion = formatCurrencyAmount(sellerShippingPrice, locale)

    const priceAfterConversion = formatCurrency(shippingPrice, {
      currency: buyerCurrency,
      locale,
    })

    if (parseFloat(shippingPrice) === 0) {
      return (
        <Cell
          title={translate('shipping')}
          body={
            <Text
              as="span"
              text={
                isIntegratedShipping
                  ? translate('shipping_description.covered_by_vinted')
                  : translate('shipping_description.covered_by_seller')
              }
            />
          }
          suffix={priceAfterConversion}
          testId={`${rootTestId}--shipping-price-coverage-info`}
        />
      )
    }

    return (
      <Cell
        title={translate('shipping')}
        body={
          <Text
            as="span"
            html
            text={translate('shipping_description.converted', { cost: priceBeforeConversion })}
            testId={`${rootTestId}--shipping-price-conversion-info`}
          />
        }
        suffix={priceAfterConversion}
      />
    )
  }

  function renderBuyerProtectionFeeInfo() {
    const { buyerCurrency } = conversion

    const formattedPrice = formatCurrency(buyerProtectionFee, {
      currency: buyerCurrency,
      locale,
    })

    return (
      <Cell
        title={translate('buyer_protection_fee')}
        suffix={formattedPrice}
        testId={`${rootTestId}--buyer-protection-fee-info`}
      />
    )
  }

  function renderExchangeRate() {
    const { sellerPrice, buyerCurrency, fxRoundedRate, fxBaseAmount } = conversion

    const base = formatCurrency(fxBaseAmount, { currency: sellerPrice.currencyCode, locale })
    const rate = formatCurrency(fxRoundedRate, { currency: buyerCurrency, locale })

    return (
      <>
        <Divider />
        <Cell>
          <Text
            as="span"
            text={`${translate('fx_rate.title')} ${translate('fx_rate.rate', { rate, base })}`}
            testId={`${rootTestId}--exchange-rate`}
          />
        </Cell>
      </>
    )
  }

  function renderPaymentServiceProviderFee() {
    const { fxMarkupRate, note } = conversion

    const feePercentage = formatNumber(parseFloat(fxMarkupRate), {
      style: 'percent',
      maximumFractionDigits: 2,
    })
    const shouldRenderNote = exchangeRateCopyCheckoutABTest?.variant === 'on' && note

    const handleNoteLinkClick = (event: MouseEvent) => {
      if (!(event.target instanceof HTMLAnchorElement)) return

      track(
        clickEvent({
          screen: 'bundle_summary',
          target: 'see_more_info_conversion_fee',
          targetDetails: JSON.stringify({ bundle_id: bundleTrackingId }),
        }),
      )
    }

    return (
      <Cell
        body={
          <Text
            as="span"
            html
            text={shouldRenderNote ? note : translate('fx_markup_rate', { fee: feePercentage })}
            testId={`${rootTestId}--payment-service-provider-fee-info`}
          />
        }
        onClick={handleNoteLinkClick}
      />
    )
  }

  function renderActions() {
    return (
      <Cell>
        <Button
          text={translate('actions.ok')}
          styling="filled"
          onClick={onClose}
          testId={`${rootTestId}--close-button`}
        />
      </Cell>
    )
  }

  return (
    <Dialog show={show} testId={rootTestId}>
      {renderNavigation()}
      <SeparatedList separator={<Divider />}>
        {renderItemPriceConversionInfo()}
        {renderShippingPriceConversionInfo()}
        {renderBuyerProtectionFeeInfo()}
      </SeparatedList>
      {renderExchangeRate()}
      {renderPaymentServiceProviderFee()}
      {renderActions()}
    </Dialog>
  )
}

export default CurrencyConversionModal
